import React, { useState, useEffect, useCallback } from "react";

// Language colors mapping
const languageColors = {
  TypeScript: "#3178c6",
  Python: "#3776ab",
  Java: "#ed8b00",
  JavaScript: "#f7df1e",
  Shell: "#89e051",
  Go: "#00ADD8",
  "C++": "#00599C",
  C: "#A8B9CC",
  HTML: "#E34C26",
  CSS: "#1572B6",
  Dockerfile: "#384D54",
  YAML: "#CB171E",
  Other: "#6e7681",
};

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

// Helper to get cached data
const getCachedData = (username) => {
  const CACHE_KEY = `github_data_${username}`;
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch (e) {
    console.error("Error reading cache:", e);
  }
  return null;
};

// Helper to cache data
const cacheData = (username, data) => {
  const CACHE_KEY = `github_data_${username}`;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  } catch (e) {
    console.error("Error caching data:", e);
  }
};

const GitHubContributions = () => {
  const username = "berhane02";
  const [isAnimated, setIsAnimated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for fetched data
  const [stats, setStats] = useState({
    totalContributions: 0,
    stars: 0,
    forks: 0,
  });
  const [commitsData, setCommitsData] = useState([]);
  const [reposPerLanguage, setReposPerLanguage] = useState([]);
  const [commitsPerLanguage, setCommitsPerLanguage] = useState([]);

  // Fetch GitHub data
  const fetchGitHubData = useCallback(async () => {
    // Check cache first
    const cachedData = getCachedData(username);
    if (cachedData) {
      setStats(cachedData.stats);
      setCommitsData(cachedData.commitsData);
      setReposPerLanguage(cachedData.reposPerLanguage);
      setCommitsPerLanguage(cachedData.commitsPerLanguage);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user repositories
      const reposResponse = await fetch(
        `https://api.github.com/users/${username}/repos?per_page=100&sort=updated`,
        {
          headers: {
            Accept: "application/vnd.github.v3+json",
          },
        }
      );

      if (!reposResponse.ok) {
        throw new Error(`GitHub API error: ${reposResponse.status}`);
      }

      const repos = await reposResponse.json();

      // Calculate stats
      let totalStars = 0;
      let totalForks = 0;
      const languageStats = {};
      const languageStars = {};
      const languageRepos = {};

      // Process repositories
      for (const repo of repos) {
        totalStars += repo.stargazers_count || 0;
        totalForks += repo.forks_count || 0;

        const lang = repo.language || "Other";
        if (!languageStats[lang]) {
          languageStats[lang] = 0;
          languageStars[lang] = 0;
          languageRepos[lang] = 0;
        }
        languageRepos[lang]++;
        languageStars[lang] += repo.stargazers_count || 0;
      }

      // Fetch commit data for each repo (limited to avoid rate limits)
      const commitPromises = repos.slice(0, 20).map(async (repo) => {
        try {
          const commitsResponse = await fetch(
            `https://api.github.com/repos/${username}/${repo.name}/commits?per_page=100&author=${username}`,
            {
              headers: {
                Accept: "application/vnd.github.v3+json",
              },
            }
          );
          if (commitsResponse.ok) {
            const commits = await commitsResponse.json();
            return commits.map((commit) => ({
              date: commit.commit.author.date,
              language: repo.language || "Other",
            }));
          }
        } catch (e) {
          console.error(`Error fetching commits for ${repo.name}:`, e);
        }
        return [];
      });

      const allCommits = (await Promise.all(commitPromises)).flat();

      // Group commits by quarter
      const quarterMap = {};
      allCommits.forEach((commit) => {
        const date = new Date(commit.date);
        const year = date.getFullYear();
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        const key = `Q${quarter} ${year}`;

        if (!quarterMap[key]) {
          quarterMap[key] = 0;
        }
        quarterMap[key]++;

        // Track commits per language
        const lang = commit.language || "Other";
        if (!languageStats[lang]) {
          languageStats[lang] = 0;
        }
        languageStats[lang]++;
      });

      // Convert to array and sort
      const commitsPerQuarter = Object.entries(quarterMap)
        .map(([quarter, commits]) => ({ quarter, commits }))
        .sort((a, b) => {
          const [qA, yA] = a.quarter.split(" ");
          const [qB, yB] = b.quarter.split(" ");
          if (yA !== yB) return parseInt(yA) - parseInt(yB);
          return parseInt(qA[1]) - parseInt(qB[1]);
        });

      // Fill missing quarters (last 7 years)
      const filledQuarters = [];
      const currentDate = new Date();
      const startYear = currentDate.getFullYear() - 6;
      
      for (let year = startYear; year <= currentDate.getFullYear(); year++) {
        for (let q = 1; q <= 4; q++) {
          const key = `Q${q} ${year}`;
          const existing = commitsPerQuarter.find((item) => item.quarter === key);
          filledQuarters.push(existing || { quarter: key, commits: 0 });
        }
      }

      setCommitsData(filledQuarters);

      // Calculate total contributions (sum of commits)
      const totalContributions = filledQuarters.reduce(
        (sum, q) => sum + q.commits,
        0
      );

      setStats({
        totalContributions,
        stars: totalStars,
        forks: totalForks,
      });

      // Calculate language distributions
      const totalRepos = Object.values(languageRepos).reduce((a, b) => a + b, 0);
      const totalCommitsForLang = Object.values(languageStats).reduce(
        (a, b) => a + b,
        0
      );

      const calculatePercentages = (data, total) => {
        return Object.entries(data)
          .map(([name, value]) => ({
            name,
            percentage: total > 0 ? Math.round((value / total) * 100) : 0,
            color: languageColors[name] || languageColors.Other,
            value,
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 6)
          .map((item) => ({
            name: item.name,
            percentage: item.percentage,
            color: item.color,
          }));
      };

      setReposPerLanguage(calculatePercentages(languageRepos, totalRepos));
      setCommitsPerLanguage(calculatePercentages(languageStats, totalCommitsForLang));

      // Cache the data
      const dataToCache = {
        stats: {
          totalContributions,
          stars: totalStars,
          forks: totalForks,
        },
        commitsData: filledQuarters,
        reposPerLanguage: calculatePercentages(languageRepos, totalRepos),
        commitsPerLanguage: calculatePercentages(languageStats, totalCommitsForLang),
      };
      cacheData(username, dataToCache);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching GitHub data:", err);
      setError(err.message);
      setLoading(false);
      
      // Use fallback data on error
      setStats({
        totalContributions: 0,
        stars: 0,
        forks: 0,
      });
      setCommitsData([]);
      setReposPerLanguage([]);
      setCommitsPerLanguage([]);
    }
  }, [username]);

  useEffect(() => {
    setIsAnimated(true);
    fetchGitHubData();
  }, [fetchGitHubData]);

  // Helper function to create donut chart path
  const createDonutPath = (startAngle, endAngle, radius, innerRadius, centerX, centerY) => {
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    const x1 = centerX + radius * Math.cos(startAngleRad);
    const y1 = centerY + radius * Math.sin(startAngleRad);
    const x2 = centerX + radius * Math.cos(endAngleRad);
    const y2 = centerY + radius * Math.sin(endAngleRad);
    
    const x3 = centerX + innerRadius * Math.cos(endAngleRad);
    const y3 = centerY + innerRadius * Math.sin(endAngleRad);
    const x4 = centerX + innerRadius * Math.cos(startAngleRad);
    const y4 = centerY + innerRadius * Math.sin(startAngleRad);
    
    const largeArcFlag = (endAngle - startAngle) > 180 ? 1 : 0;
    
    return [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
      "Z",
    ].join(" ");
  };

  // Render donut chart component
  const DonutChart = ({ title, data, delay = 0 }) => {
    let currentAngle = -90;
    const donutRadius = 35;
    const donutInnerRadius = 22;
    const donutCenterX = 60;
    const donutCenterY = 60;

    return (
      <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/30 p-2 md:p-3">
        <h5 className="text-xs font-semibold text-gray-300 mb-2 md:mb-3 text-center">{title}</h5>
        <div className="flex flex-col items-center gap-2 md:gap-3">
          <div className="relative w-28 h-28 md:w-32 md:h-32 flex-shrink-0">
            <svg viewBox="0 0 120 120" className="w-full h-full">
              {data.map((item, index) => {
                const startAngle = currentAngle;
                const endAngle = currentAngle + (item.percentage / 100) * 360;
                const pathData = createDonutPath(
                  startAngle,
                  endAngle,
                  donutRadius,
                  donutInnerRadius,
                  donutCenterX,
                  donutCenterY
                );
                currentAngle = endAngle;
                
                return (
                  <path
                    key={index}
                    d={pathData}
                    fill={item.color}
                    className="hover:opacity-80 transition-opacity cursor-pointer"
                    style={{
                      opacity: isAnimated ? 1 : 0,
                      transform: isAnimated ? "scale(1)" : "scale(0)",
                      transformOrigin: `${donutCenterX}px ${donutCenterY}px`,
                      transition: `opacity 0.5s ease ${index * 50 + delay}ms, transform 0.5s ease ${index * 50 + delay}ms`,
                    }}
                  />
                );
              })}
            </svg>
          </div>
          
          {/* Legend */}
          <div className="flex flex-col gap-1 w-full">
            {data.map((item, index) => (
              <div 
                key={index} 
                className="flex items-center gap-1.5 transition-opacity duration-500"
                style={{ opacity: isAnimated ? 1 : 0, transitionDelay: `${index * 50 + delay + 300}ms` }}
              >
                <div
                  className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-[10px] md:text-xs text-gray-300">{item.name}</span>
                <span className="text-[10px] md:text-xs text-gray-500 ml-auto">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setIsAnimated(true);
  }, []);

  // Calculate max commits for graph scaling
  const maxCommits = commitsData.length > 0 
    ? Math.max(...commitsData.map(q => q.commits), 1) 
    : 1;

  if (loading) {
    return (
      <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-700/50">
        <div className="mb-4">
          <h4 className="text-base md:text-lg font-semibold text-gray-300 mb-1 flex items-center gap-2">
            <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            GitHub Activity Dashboard
          </h4>
        </div>
        <div className="bg-gray-900/40 backdrop-blur-sm rounded-lg border border-gray-700/40 p-3 md:p-4 flex items-center justify-center h-64">
          <div className="text-gray-400 text-sm">Loading GitHub data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-700/50">
        <div className="mb-4">
          <h4 className="text-base md:text-lg font-semibold text-gray-300 mb-1 flex items-center gap-2">
            <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            GitHub Activity Dashboard
          </h4>
        </div>
        <div className="bg-gray-900/40 backdrop-blur-sm rounded-lg border border-red-700/40 p-3 md:p-4">
          <div className="text-red-400 text-sm">Error loading GitHub data: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-700/50">
      <div className="mb-4">
        <h4 className="text-base md:text-lg font-semibold text-gray-300 mb-1 flex items-center gap-2">
          <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
          </svg>
          GitHub Activity Dashboard
        </h4>
        <p className="text-gray-400 text-xs font-light">
          Comprehensive coding activity and repository statistics
        </p>
      </div>

      {/* Unified Dashboard Card */}
      <div className="bg-gray-900/40 backdrop-blur-sm rounded-lg border border-gray-700/40 hover:border-green-500/30 transition-all duration-300 p-3 md:p-4 overflow-hidden relative">
        {/* Background gradient animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-blue-500/5 to-purple-500/5 animate-pulse opacity-50"></div>
        
        <div className="relative z-10 space-y-4 md:space-y-5">
          {/* Header with Stats */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-3">
            <div>
              <div className="text-lg md:text-xl font-bold text-white">
                {stats.totalContributions.toLocaleString()} contributions
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-5">
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span className="text-sm md:text-base font-semibold text-gray-300">{stats.stars}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                <span className="text-sm md:text-base font-semibold text-gray-300">{stats.forks}</span>
              </div>
            </div>
          </div>

          {/* Commits per Quarter Line Graph */}
          {commitsData.length > 0 && (
            <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/30 p-3 md:p-4">
              <h5 className="text-sm font-semibold text-gray-300 mb-2 md:mb-3">Commits per quarter</h5>
              <div className="relative w-full h-32 md:h-36">
                <svg viewBox="0 0 1200 150" className="w-full h-full" preserveAspectRatio="none">
                  {/* Y-axis grid lines */}
                  {[0, Math.ceil(maxCommits * 0.25), Math.ceil(maxCommits * 0.5), Math.ceil(maxCommits * 0.75), maxCommits].map((value) => {
                    const y = 130 - (value / maxCommits) * 120;
                    return (
                      <g key={value}>
                        <line
                          x1="50"
                          y1={y}
                          x2="1150"
                          y2={y}
                          stroke="rgba(75, 85, 99, 0.2)"
                          strokeWidth="1"
                          strokeDasharray="2,2"
                        />
                        <text
                          x="45"
                          y={y + 4}
                          textAnchor="end"
                          className="text-[10px] fill-gray-500"
                        >
                          {value}
                        </text>
                      </g>
                    );
                  })}

                  {/* X-axis labels (show every 4th quarter) */}
                  {commitsData.map((quarter, index) => {
                    if (index % 4 !== 0 && index !== commitsData.length - 1) return null;
                    const x = 50 + (index / Math.max(commitsData.length - 1, 1)) * 1100;
                    return (
                      <text
                        key={index}
                        x={x}
                        y="145"
                        textAnchor="middle"
                        className="text-[9px] fill-gray-500"
                        transform={`rotate(-45 ${x} 145)`}
                      >
                        {quarter.quarter}
                      </text>
                    );
                  })}

                  {/* Line path */}
                  <polyline
                    points={commitsData.map((quarter, index) => {
                      const x = 50 + (index / Math.max(commitsData.length - 1, 1)) * 1100;
                      const y = 130 - (quarter.commits / maxCommits) * 120;
                      return `${x},${y}`;
                    }).join(" ")}
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="2.5"
                    className="transition-opacity duration-1000"
                    style={{ opacity: isAnimated ? 1 : 0 }}
                  />

                  {/* Data points */}
                  {commitsData.map((quarter, index) => {
                    const x = 50 + (index / Math.max(commitsData.length - 1, 1)) * 1100;
                    const y = 130 - (quarter.commits / maxCommits) * 120;
                    return (
                      <g key={index}>
                        <circle
                          cx={x}
                          cy={y}
                          r="4"
                          fill="#22c55e"
                          className="hover:r-6 transition-all duration-300 cursor-pointer"
                          style={{
                            opacity: isAnimated ? 1 : 0,
                            transition: `opacity 0.3s ease ${index * 20}ms`,
                          }}
                        />
                        <circle
                          cx={x}
                          cy={y}
                          r="6"
                          fill="#22c55e"
                          opacity="0.3"
                          className="hover:opacity-50 transition-opacity duration-300 cursor-pointer"
                          style={{
                            opacity: isAnimated ? 0.3 : 0,
                            transition: `opacity 0.3s ease ${index * 20}ms`,
                          }}
                        />
                      </g>
                    );
                  })}
                </svg>
              </div>
            </div>
          )}

          {/* Charts Grid */}
          {(reposPerLanguage.length > 0 || stats.stars > 0 || commitsPerLanguage.length > 0) && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
              {reposPerLanguage.length > 0 && (
                <DonutChart title="Repos per Language" data={reposPerLanguage} delay={0} />
              )}
              {/* Overall Stars Display */}
              {stats.stars > 0 && (
                <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/30 p-2 md:p-3">
                  <h5 className="text-xs font-semibold text-gray-300 mb-2 md:mb-3 text-center">Overall Stars</h5>
                  <div className="flex flex-col items-center justify-center gap-2 md:gap-3 h-28 md:h-32">
                    <div className="flex items-center gap-2">
                      <svg className="w-8 h-8 md:w-10 md:h-10 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <div 
                        className="text-2xl md:text-3xl font-bold text-white transition-opacity duration-500"
                        style={{ opacity: isAnimated ? 1 : 0 }}
                      >
                        {stats.stars.toLocaleString()}
                      </div>
                    </div>
                    <div 
                      className="text-xs md:text-sm text-gray-400 transition-opacity duration-500"
                      style={{ opacity: isAnimated ? 1 : 0, transitionDelay: '200ms' }}
                    >
                      Total Stars Received
                    </div>
                  </div>
                </div>
              )}
              {commitsPerLanguage.length > 0 && (
                <DonutChart title="Commits per Language" data={commitsPerLanguage} delay={400} />
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Link to GitHub */}
      <div className="mt-4 text-center">
        <a
          href={`https://github.com/${username}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-green-400 hover:text-green-300 text-xs md:text-sm font-medium transition-colors duration-300 group"
        >
          <span className="group-hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.6)] transition-all duration-300">View GitHub Profile</span>
          <svg className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default GitHubContributions;
